import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { listDocuments, ingestDocument } from "@/lib/atlasApi";
import { Upload, RefreshCw, FileText } from "lucide-react";

const STATUS_COLORS = {
  PENDING: "#F59E0B", PROCESSING: "#00E5FF", COMPLETED: "#00FF9D", FAILED: "#FF007A",
};

export default function LibraryPanel({ isAdmin }) {
  const [docs, setDocs] = useState([]);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try { const d = await listDocuments(); setDocs(d.documents || []); } catch { /* noop */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      await ingestDocument(file, { title });
      setFile(null); setTitle("");
      setTimeout(refresh, 800);
    } catch (e) {
      setError(e?.response?.data?.code || "Falha ao ingerir o documento.");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-4 space-y-4" data-testid="library-panel">
      {isAdmin && (
        <div className="rounded-lg border border-[#1F2937] bg-[#0B0F19] p-3 space-y-2">
          <div className="text-xs uppercase tracking-wider text-slate-500">Ingerir documento (admin)</div>
          <Input
            data-testid="library-title-input"
            placeholder="Título (opcional)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="bg-[#111827] border-[#1F2937]"
          />
          <input
            data-testid="library-file-input"
            type="file"
            accept=".pdf,.txt,.md,.markdown"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full text-xs text-slate-400 file:mr-3 file:rounded-md file:border-0 file:bg-[#FF6B2B] file:px-3 file:py-1.5 file:text-white file:font-medium"
          />
          <Button data-testid="library-upload-btn" onClick={upload} disabled={!file || busy} className="w-full bg-[#FF6B2B] text-white hover:bg-[#E05A2B]">
            <Upload className="h-4 w-4 mr-2" />{busy ? "Enviando…" : "Ingerir no acervo"}
          </Button>
          {error && <p className="text-xs text-[#FF007A]" data-testid="library-error">{error}</p>}
        </div>
      )}

      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-300">Acervo ({docs.length})</span>
        <Button data-testid="library-refresh-btn" variant="ghost" size="sm" onClick={refresh} className="text-slate-400">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-2">
        {docs.length === 0 && <p className="text-sm text-slate-500" data-testid="library-empty">Nenhum documento indexado.</p>}
        {docs.map((d) => (
          <div key={d.id} className="rounded-lg border border-[#1F2937] bg-[#0B0F19] p-3" data-testid={`library-doc-${d.id}`}>
            <div className="flex items-start gap-2">
              <FileText className="h-4 w-4 mt-0.5 text-[#00E5FF] shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-200 truncate">{d.title || d.filename}</div>
                <div className="mt-1 flex items-center gap-2">
                  <Badge style={{ backgroundColor: `${STATUS_COLORS[d.status] || "#94A3B8"}22`, color: STATUS_COLORS[d.status] || "#94A3B8" }}>
                    {d.status}
                  </Badge>
                  {d.chunk_count > 0 && <span className="text-[11px] text-slate-500">{d.chunk_count} chunks</span>}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
